import { ACTIVITY_LANGUAGES, PARTICIPATIONS, rangeMatches } from '@yoyoclub/shared';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import FollowHostButton from '../components/FollowHostButton';
import ActivitiesFilters from '../components/activities/ActivitiesFilters';
import ActivitiesFiltersSheet from '../components/activities/ActivitiesFiltersSheet';
import {
  ADULT_ONLY,
  EMPTY_FILTERS,
  FilterState,
  activeFilterCount,
  isAgeActive,
  isFilterActive,
  sanitizeAge,
} from '../components/activities/filterState';
import HostReviews from '../components/reviews/HostReviews';
import { ActivityCalendarView, ActivityCard } from '../components/ui';
import { courseApi, type PublicCourse } from '../services/courseApi';
import { transformActivity, transformCourse } from '../utils/activityTransform';
import { ptDayEndUtcMs, ptDayStartUtcMs } from '../utils/timezone';
import { resolveTimePreset } from '../utils/whenRange';

// Activity type from backend API
interface Activity {
  id: string;
  titleCN: string | null;
  titleEN: string | null;
  slug: string;
  category: string;
  location: string;
  locationDetails: string;
  region: string[];
  ageMin: number;
  ageMax: number;
  price: number;
  adultPrice: number;
  childRequired?: boolean;
  maxParticipants: number;
  duration: number;
  status: string;
  host: {
    displayName: string;
    id: string;
  };
  images: Array<{
    imageUrl: string;
    order: number;
    isCover: boolean;
  }>;
  sessions: Array<{
    id: string;
    startTime: string;
    endTime: string;
    maxCapacity: number;
    currentBookings: number;
    price?: number | null;
    adultPrice?: number | null;
  }>;
}

type TabType = 'upcoming' | 'calendar' | 'past';

const VALID_REGIONS = new Set(['SOUTH_BAY', 'PENINSULA', 'EAST_BAY', 'SAN_FRANCISCO', 'OTHER']);
const VALID_CATEGORIES = new Set([
  'ART',
  'MUSIC',
  'DANCE',
  'SPORTS',
  'COOKING',
  'STEM',
  'NATURE',
  'OTHER',
]);

/**
 * Read filter state from URL params (new protocol + legacy normalization).
 *   - Legacy `category=MUSIC` → `categories=MUSIC`
 *   - Legacy `date=YYYY-MM-DD` → `dateFrom=dateTo=date`, whenPreset='custom'
 *   - Legacy `age=0-2` segment format → dropped silently
 *   - Invalid enum values dropped silently
 *
 * Returns the parsed state and a boolean indicating whether the URL should be
 * rewritten to the canonical form (replace history).
 */
const VALID_WHEN: ReadonlySet<FilterState['whenPreset']> = new Set([
  'anytime',
  'today',
  'weekend',
  'next7days',
  'thisMonth',
  'custom',
]);
const VALID_LANGUAGES: ReadonlySet<string> = new Set(ACTIVITY_LANGUAGES);
const VALID_PARTICIPATIONS: ReadonlySet<string> = new Set(PARTICIPATIONS);

/** Non-negative finite number from a URL param, else undefined. */
function parseAgeParam(raw: string | null): number | undefined {
  if (raw == null || raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

function parseUrl(searchParams: URLSearchParams): { state: FilterState; needsRewrite: boolean } {
  let needsRewrite = false;

  // categories: new wins; fall back to legacy single-value `category`.
  const categoriesParam = searchParams.get('categories');
  const legacyCategory = searchParams.get('category');
  let categories: string[] = [];
  if (categoriesParam) {
    categories = categoriesParam.split(',').filter((c) => VALID_CATEGORIES.has(c));
  } else if (legacyCategory && VALID_CATEGORIES.has(legacyCategory)) {
    categories = [legacyCategory];
    needsRewrite = true;
  } else if (legacyCategory) {
    needsRewrite = true; // strip invalid legacy param
  }

  // regions: csv enum
  const regionsParam = searchParams.get('regions');
  const regions = regionsParam ? regionsParam.split(',').filter((r) => VALID_REGIONS.has(r)) : [];

  // Dates: new wins; legacy `date` aliases to dateFrom=dateTo=date.
  let dateFrom = searchParams.get('dateFrom') || '';
  let dateTo = searchParams.get('dateTo') || '';
  const legacyDate = searchParams.get('date');
  if (!dateFrom && !dateTo && legacyDate) {
    dateFrom = legacyDate;
    dateTo = legacyDate;
    needsRewrite = true;
  } else if (legacyDate) {
    needsRewrite = true; // strip legacy `date` if new is also set
  }

  // Age: drop legacy segment format like "0-2".
  // Age: whole years 0..99; an old "1.5" or out-of-range value is normalized in the URL.
  let age = searchParams.get('age') || '';
  if (age) {
    const clean = sanitizeAge(age);
    if (clean !== age) {
      age = clean;
      needsRewrite = true;
    }
  }

  // When preset: `when=weekend|next7days|thisMonth` is persisted explicitly so presets
  // survive across reloads. `custom` is inferred from dateFrom/dateTo. `anytime` is the
  // default and is not written to the URL.
  const whenRaw = searchParams.get('when');
  let whenPreset: FilterState['whenPreset'];
  if (whenRaw && VALID_WHEN.has(whenRaw as FilterState['whenPreset'])) {
    whenPreset = whenRaw as FilterState['whenPreset'];
  } else {
    whenPreset = dateFrom || dateTo ? 'custom' : 'anytime';
    if (whenRaw && !VALID_WHEN.has(whenRaw as FilterState['whenPreset'])) needsRewrite = true;
  }

  // Language and participation (PRD §4.3): csv enums.
  const languagesParam = searchParams.get('lang');
  const languages = languagesParam
    ? languagesParam.split(',').filter((l) => VALID_LANGUAGES.has(l))
    : [];
  const participationParam = searchParams.get('participation');
  const participations = participationParam
    ? participationParam.split(',').filter((v) => VALID_PARTICIPATIONS.has(v))
    : [];

  // Legacy `audience=adult` is now the ADULT_ONLY participation option.
  if (searchParams.get('audience') === 'adult') {
    if (!participations.includes(ADULT_ONLY)) participations.push(ADULT_ONLY);
    needsRewrite = true;
  } else if (searchParams.get('audience') !== null) {
    needsRewrite = true;
  }
  if (participations.includes(ADULT_ONLY)) age = ''; // a child age cannot match adult-only

  // Stage range from a homepage deep link: ageMin (inclusive), ageMax (exclusive, optional).
  const rangeLo = parseAgeParam(searchParams.get('ageMin'));
  const rangeHi = parseAgeParam(searchParams.get('ageMax'));
  let ageRange: FilterState['ageRange'] = null;
  if (
    age === '' &&
    !participations.includes(ADULT_ONLY) &&
    (rangeLo !== undefined || rangeHi !== undefined)
  ) {
    if (rangeHi === undefined || (rangeLo ?? 0) < rangeHi) {
      ageRange = { lo: rangeLo ?? 0, hi: rangeHi };
    } else {
      needsRewrite = true;
    }
  } else if (searchParams.get('ageMin') !== null || searchParams.get('ageMax') !== null) {
    needsRewrite = true; // a specific age (or adult-only) replaces the range
  }

  const audience: FilterState['audience'] = age !== '' ? 'child' : 'any';

  return {
    state: {
      whenPreset,
      dateFrom,
      dateTo,
      audience,
      age,
      ageRange,
      regions,
      categories,
      languages,
      participations,
    },
    needsRewrite,
  };
}

/** Convert FilterState into URL params (canonical form). */
function filtersToUrl(filters: FilterState, base: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams();
  const type = base.get('type');
  if (type) next.set('type', type);
  const hostId = base.get('hostId');
  if (hostId) next.set('hostId', hostId);

  if (filters.regions.length > 0) next.set('regions', filters.regions.join(','));
  if (filters.categories.length > 0) next.set('categories', filters.categories.join(','));
  if (isAgeActive(filters)) {
    next.set('age', filters.age.trim());
  } else if (filters.ageRange) {
    next.set('ageMin', String(filters.ageRange.lo));
    if (filters.ageRange.hi !== undefined) next.set('ageMax', String(filters.ageRange.hi));
  }
  if (filters.languages.length > 0) next.set('lang', filters.languages.join(','));
  if (filters.participations.length > 0)
    next.set('participation', filters.participations.join(','));

  // Persist When preset explicitly. Only `custom` carries dateFrom/dateTo;
  // dynamic presets (weekend / next7days / thisMonth) re-resolve on read so the
  // window always reflects the user's "today" rather than a frozen date.
  if (filters.whenPreset === 'custom') {
    if (filters.dateFrom) next.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) next.set('dateTo', filters.dateTo);
    next.set('when', 'custom');
  } else if (filters.whenPreset !== 'anytime') {
    next.set('when', filters.whenPreset);
  }
  return next;
}

/** Resolve a When preset to absolute PT date range. */
function resolveWhenRange(filters: FilterState): { from?: string; to?: string } {
  return resolveTimePreset(filters.whenPreset, filters);
}

export default function AllActivities() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation('activities');
  const rawType = searchParams.get('type') || 'upcoming';
  const type: TabType =
    rawType === 'past' || rawType === 'calendar' ? (rawType as TabType) : 'upcoming';

  const hostId = searchParams.get('hostId') || '';

  const parsed = useMemo(() => parseUrl(searchParams), [searchParams]);
  const filters = parsed.state;

  // Canonicalize URL once on mount if legacy params were detected.
  useEffect(() => {
    if (parsed.needsRewrite) {
      setSearchParams(filtersToUrl(filters, searchParams), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilters = (next: FilterState) => {
    setSearchParams(filtersToUrl(next, searchParams));
  };

  const [activities, setActivities] = useState<Activity[]>([]);
  const [courses, setCourses] = useState<PublicCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [hostName, setHostName] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Filter only applies on the upcoming tab. Calendar / past show unfiltered data —
  // but filter state in URL is preserved so users return to their selection when they
  // switch back to upcoming. We'll enable filter on calendar later if volume warrants it.
  const effectiveFilters = type === 'upcoming' ? filters : EMPTY_FILTERS;

  const apiUrl = useMemo(() => {
    const base = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
    const qs = new URLSearchParams();
    if (hostId) qs.set('hostId', hostId);
    if (effectiveFilters.regions.length > 0) qs.set('regions', effectiveFilters.regions.join(','));
    if (effectiveFilters.categories.length > 0)
      qs.set('categories', effectiveFilters.categories.join(','));
    const range = resolveWhenRange(effectiveFilters);
    if (range.from) qs.set('dateFrom', range.from);
    if (range.to) qs.set('dateTo', range.to);
    if (isAgeActive(effectiveFilters)) {
      qs.set('age', effectiveFilters.age.trim());
    } else if (effectiveFilters.ageRange) {
      qs.set('ageMin', String(effectiveFilters.ageRange.lo));
      if (effectiveFilters.ageRange.hi !== undefined)
        qs.set('ageMax', String(effectiveFilters.ageRange.hi));
    }
    if (effectiveFilters.languages.length > 0) qs.set('lang', effectiveFilters.languages.join(','));
    if (effectiveFilters.participations.length > 0)
      qs.set('participation', effectiveFilters.participations.join(','));
    const q = qs.toString();
    return `${base}/activities${q ? `?${q}` : ''}`;
  }, [effectiveFilters, hostId]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    fetch(apiUrl, { signal: controller.signal })
      .then((res) => res.json())
      .catch(() => ({ success: false }))
      .then((actRes) => {
        if (cancelled) return; // stale response — newer request supersedes us
        if (actRes.success && actRes.data) {
          setActivities(actRes.data);
          if (hostId && actRes.data.length > 0) {
            setHostName(actRes.data[0].host?.displayName || null);
          }
        } else {
          setActivities([]);
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [apiUrl, hostId]);

  // Recorded courses share this catalog (no standalone page). The public course
  // list is small and not date/region-scoped, so we fetch it once and merge it
  // into the upcoming grid below.
  useEffect(() => {
    let cancelled = false;
    courseApi
      .listCourses()
      .then((data) => {
        if (!cancelled) setCourses(data);
      })
      .catch(() => {
        if (!cancelled) setCourses([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setTab = (next: TabType) => {
    const params = filtersToUrl(filters, searchParams);
    params.set('type', next);
    if (hostId) params.set('hostId', hostId);
    setSearchParams(params);
  };

  // Upcoming = at least one session that has not ended; past = sessions, all over.
  // Same rule as the homepage (PRD S2), so its "view all" links list the same set.
  const nowMs = Date.now();
  const hasOpenSession = (activity: (typeof activities)[number]) =>
    activity.sessions.some((s) => new Date(s.endTime).getTime() > nowMs);

  const upcomingActivities = activities.filter(
    (activity) => activity.sessions?.length > 0 && hasOpenSession(activity)
  );

  const pastActivities = activities.filter(
    (activity) => activity.sessions?.length > 0 && !hasOpenSession(activity)
  );

  const currentLocale = i18n.language;

  // Display-session window: when a When filter is active, cards prefer sessions
  // inside the requested PT range. Use ptDayStartUtcMs/ptDayEndUtcMs so the window
  // matches the backend's PT-boundary filter regardless of browser timezone.
  // Uses effectiveFilters — calendar/past intentionally see an empty window.
  const range = resolveWhenRange(effectiveFilters);
  const windowMs = useMemo(() => {
    const startNum = range.from ? ptDayStartUtcMs(range.from) : NaN;
    const endNum = range.to ? ptDayEndUtcMs(range.to) : NaN;
    return {
      startMs: Number.isFinite(startNum) ? startNum : undefined,
      endMs: Number.isFinite(endNum) ? endNum : undefined,
    };
  }, [range.from, range.to]);

  const transformedUpcoming = upcomingActivities
    .map((activity) => transformActivity(activity, currentLocale, windowMs))
    .filter((item): item is NonNullable<typeof item> => item !== null);
  const transformedPast = pastActivities
    .map((activity) => transformActivity(activity, currentLocale, windowMs))
    .filter((item): item is NonNullable<typeof item> => item !== null);

  // Activities-only — passes have been removed from this page (PRD §1.2).
  const cardItems = type === 'past' ? transformedPast : transformedUpcoming;

  // Recorded courses appear only on the upcoming tab, after the in-person activities.
  // They can be watched any time from anywhere, so date and region filters never hide
  // them (PRD §4.3, §3.5); category, language, participation and age still apply,
  // using the same rules as the backend (effective values come from the API).
  const courseCardItems = useMemo(() => {
    if (type !== 'upcoming') return [];
    const f = effectiveFilters;
    const cats = f.categories;
    const age = isAgeActive(f) ? Number(f.age) : undefined;
    return courses
      .filter((c) => (hostId ? c.host?.id === hostId : true))
      .filter((c) => (cats.length > 0 ? cats.includes(c.category) : true))
      .filter((c) => (f.languages.length > 0 ? f.languages.includes(c.language) : true))
      .filter((c) =>
        f.participations.length > 0 ? f.participations.includes(c.participation) : true
      )
      .filter((c) =>
        f.ageRange ? rangeMatches(c, f.ageRange.lo, f.ageRange.hi ?? Infinity) : true
      )
      .filter((c) =>
        age !== undefined
          ? c.participation !== ADULT_ONLY && c.ageMin <= age && c.ageMax >= age
          : true
      )
      .map((c) => transformCourse(c, currentLocale));
  }, [type, effectiveFilters, courses, hostId, currentLocale]);

  const filterActive = isFilterActive(effectiveFilters);
  const filterCount = activeFilterCount(effectiveFilters);

  const metaTitle =
    type === 'past'
      ? t('meta.titlePast')
      : type === 'calendar'
        ? t('meta.titleCalendar')
        : t('meta.title');
  const metaDescription =
    type === 'past'
      ? t('meta.descriptionPast')
      : type === 'calendar'
        ? t('meta.descriptionCalendar')
        : t('meta.description');

  const renderTab = (key: TabType, label: string) => (
    <button
      onClick={() => setTab(key)}
      className={`px-6 py-3 font-medium transition-colors relative ${
        type === key ? 'text-primary' : 'text-gray-600 hover:text-gray-900'
      }`}
    >
      {label}
      {type === key && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
    </button>
  );

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
      </Helmet>

      <div className="bg-gray-50 min-h-screen py-8">
        <div className="container-custom">
          {/* Stacks on mobile: a long host name next to the actions would
              otherwise overflow the row on a narrow screen. */}
          <div className="flex flex-col items-start gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 min-w-0 break-words">
              {hostId && hostName ? t('activitiesBy', { hostName }) : t('heading')}
            </h1>
            {hostId && (
              <div className="flex items-center gap-4 shrink-0">
                <FollowHostButton hostId={hostId} variant="primary" />
                <Link to="/activities" className="text-sm text-primary hover:underline">
                  {t('viewAll')}
                </Link>
              </div>
            )}
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2 mb-6 overflow-x-auto items-center">
            {renderTab('upcoming', t('tabs.upcoming'))}
            {renderTab('calendar', t('tabs.calendar'))}
            {renderTab('past', t('tabs.past'))}
          </div>

          {/* Desktop filter bar — only shown on the upcoming tab; calendar/past tabs
              ignore filter state for now (state preserved in URL for tab switching). */}
          {type === 'upcoming' && (
            <div className="hidden md:block mb-6">
              <ActivitiesFilters value={filters} onChange={setFilters} />
            </div>
          )}

          {/* Mobile filter button — only shown on the upcoming list; calendar/past
              don't need filtering and shouldn't expose a chrome the user can't act on. */}
          {type === 'upcoming' && (
            <div className="md:hidden mb-4 flex justify-end">
              <button
                type="button"
                onClick={() => setSheetOpen(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  filterActive
                    ? 'bg-primary-pale border-primary text-primary'
                    : 'bg-white border-soft-gray text-charcoal'
                }`}
              >
                {filterCount > 0
                  ? t('filters.mobileButton', { count: filterCount })
                  : t('filters.mobileButtonNone')}
              </button>
            </div>
          )}

          {/* Mobile bottom sheet */}
          <ActivitiesFiltersSheet
            open={sheetOpen}
            value={filters}
            onClose={() => setSheetOpen(false)}
            onApply={setFilters}
          />

          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">{t('loading')}</p>
            </div>
          ) : type === 'calendar' ? (
            <ActivityCalendarView
              // Re-mount calendar when active filter changes so navigation state (current month
              // / selected date) resets to match the new window.
              key={`${hostId || 'all'}|${filterCount}|${effectiveFilters.whenPreset}|${effectiveFilters.dateFrom}|${effectiveFilters.dateTo}|${effectiveFilters.regions.join(',')}|${effectiveFilters.categories.join(',')}|${effectiveFilters.audience}|${effectiveFilters.age}`}
              activities={
                // Trim each activity's sessions to only those falling in the active When window
                // and matching cancel state, so the calendar doesn't show sessions outside the filter.
                filterActive && (windowMs.startMs !== undefined || windowMs.endMs !== undefined)
                  ? upcomingActivities
                      .map((a) => ({
                        ...a,
                        sessions: a.sessions.filter((s) => {
                          const t = new Date(s.startTime).getTime();
                          if (windowMs.startMs !== undefined && t < windowMs.startMs) return false;
                          if (windowMs.endMs !== undefined && t > windowMs.endMs) return false;
                          return true;
                        }),
                      }))
                      .filter((a) => a.sessions.length > 0)
                  : upcomingActivities
              }
            />
          ) : cardItems.length === 0 && courseCardItems.length === 0 ? (
            <div className="text-center py-12">
              {filterActive ? (
                <>
                  <h3 className="text-lg font-semibold text-charcoal mb-2">
                    {t('emptyFiltered.title')}
                  </h3>
                  <p className="text-sm text-gray-500 mb-5">{t('emptyFiltered.hint')}</p>
                  <button
                    type="button"
                    onClick={() => setFilters(EMPTY_FILTERS)}
                    className="px-5 py-2 bg-primary text-white rounded-full text-sm font-medium hover:bg-primary-dark"
                  >
                    {t('emptyFiltered.clear')}
                  </button>
                </>
              ) : (
                <p className="text-gray-600">{type === 'past' ? t('noPast') : t('noUpcoming')}</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {cardItems.map((item) => (
                <ActivityCard key={`activity-${item.id}`} {...item} />
              ))}
              {courseCardItems.map((item) => (
                <ActivityCard key={`course-${item.id}`} {...item} />
              ))}
            </div>
          )}

          {/* Host reviews: aggregated across all of this host's activities.
              key forces a clean remount when switching hosts (no stale data). */}
          {hostId && <HostReviews key={hostId} hostId={hostId} />}
        </div>
      </div>
    </>
  );
}
